import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Mail, CalendarClock, Inbox } from "lucide-react";
import { http } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import DashShell from "@/components/dash/DashShell";
import NotificationBell from "@/components/dash/NotificationBell";
import { Card, StatusPill } from "@/components/portal/ui";
import { money } from "@/lib/constants";

const NAMES = { diagnostic: "Clarity", blueprint: "Blueprint", guided: "Guided Virtual Consulting", inhome_overnight: "In-Home + Overnight" };
export const OP_LINKS = (active) => [
  ["/operator", "Dashboard", active === "dashboard"],
  ["/operator/clients", "Clients", active === "clients"],
  ["/operator/inbox", "Inbox", active === "inbox"],
  ["/operator/orders", "Orders", active === "orders"],
  ["/operator/profile", "Profile", active === "profile"],
];

function Queue({ title, items, tone, nav, empty }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg text-[#1A1918]">{title}</h3>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${tone}`}>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-6" data-testid={`empty-${title.toLowerCase().replace(/\s+/g, "-")}`}>
          <Inbox size={18} className="text-[#C9C3B8] mx-auto mb-2" />
          <p className="text-[#8A998C] text-sm">{empty}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((s) => (
            <button key={s.id} data-testid={`queue-item-${s.customer_id}-${s.product_key}`} onClick={() => nav(`/operator/customer/${s.customer_id}`)}
              className="w-full text-left border border-[#F0ECE4] rounded-lg p-3 hover:border-[#9F634E]/40 transition-colors flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[#1A1918] text-sm font-medium flex items-center gap-2 flex-wrap">
                  {s.customer_name}
                  {s.is_test && <span className="text-[9px] uppercase tracking-wide bg-[#C49A5A]/20 text-[#8a6a2e] rounded-full px-1.5 py-0.5">Test</span>}
                  {s.safety_flags?.length > 0 && <AlertTriangle size={13} className="text-[#9E4F4F]" />}
                  {s.unread > 0 && <span data-testid={`unread-${s.customer_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-[#9F634E] text-white rounded-full px-1.5 py-0.5"><Mail size={9} /> {s.unread}</span>}
                </div>
                <div className="text-[#6B665E] text-xs">{NAMES[s.product_key]}{s.child_name ? ` · ${s.child_name}` : ""}{s.child_age != null ? ` · ${s.child_age} mo` : ""}</div>
                <div className="text-[#8A998C] text-xs mt-0.5 truncate">{s.next_action}</div>
                {s.product_key === "inhome_overnight" && s.scheduled_date && (
                  <div className="text-[#4d6b4d] text-xs mt-0.5 flex items-center gap-1"><CalendarClock size={11} /> {s.scheduled_date}{s.reminder_status ? ` · reminder ${s.reminder_status}` : ""}</div>
                )}
                {s.product_key === "guided" && s.waiting_on && (
                  <div className={`text-xs mt-0.5 ${s.waiting_on === "consultant" ? "text-[#9F634E]" : "text-[#8A998C]"}`}>Waiting on {s.waiting_on}</div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0"><StatusPill state={s.state} /><ArrowRight size={14} className="text-[#8A998C]" /></div>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value, tone, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="eyebrow text-[#8A998C] mb-1.5">{label}</div>
      <div className={`font-display text-2xl ${tone || "text-[#1A1918]"}`}>{value}</div>
    </Card>
  );
}

export default function OperatorDashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useSeo({ title: "Consultant Dashboard", noindex: true });
  useEffect(() => { http.get("/operator/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);

  const links = OP_LINKS("dashboard");
  if (!d) return <DashShell brandLabel="Consultant Workspace" links={links} rightSlot={<NotificationBell />}><p className="text-[#6B665E]">Loading…</p></DashShell>;
  const st = d.stats || {};
  const allEmpty = [d.new, d.needs_action, d.active, d.follow_up, d.completed].every((q) => !q || q.length === 0);

  return (
    <DashShell brandLabel="Consultant Workspace" links={links} rightSlot={<NotificationBell />}>
      <div className="mb-6">
        <div className="eyebrow text-[#8A998C] mb-1">Consultant workspace</div>
        <h1 className="font-display text-3xl text-[#1A1918]">Your live dashboard</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="summary-cards">
        <Stat testid="stat-new-orders" label="New Orders" value={st.new_orders ?? 0} tone="text-[#9F634E]" />
        <Stat testid="stat-active" label="Active Clients" value={st.active_clients ?? 0} />
        <Stat testid="stat-awaiting-consultant" label="Awaiting Consultant" value={st.awaiting_consultant ?? 0} tone="text-[#8a6a2e]" />
        <Stat testid="stat-awaiting-client" label="Awaiting Client" value={st.awaiting_client ?? 0} />
        <Stat testid="stat-completed" label="Completed" value={st.completed ?? 0} />
        <Stat testid="stat-revenue" label="Revenue" value={money(st.revenue ?? 0)} tone="text-[#4d6b4d]" />
      </div>

      {d.flags?.length > 0 && (
        <Card className="p-4 mb-6 border-[#9E4F4F]/40 bg-[#9E4F4F]/5">
          <div className="flex items-center gap-2 text-[#9E4F4F] font-medium text-sm"><AlertTriangle size={16} /> {d.flags.length} customer(s) with safety flags — review before progressing.</div>
        </Card>
      )}

      {allEmpty ? (
        <Card className="p-10 text-center" data-testid="dashboard-empty">
          <Inbox size={26} className="text-[#C9C3B8] mx-auto mb-3" />
          <h3 className="font-display text-xl text-[#1A1918] mb-1">No clients yet</h3>
          <p className="text-[#6B665E] text-sm max-w-md mx-auto">New customers will appear here automatically after their first successful purchase and intake. You're all caught up.</p>
        </Card>
      ) : (
        <>
          <h2 className="font-display text-xl text-[#1A1918] mb-3">Needs your attention</h2>
          <div className="grid lg:grid-cols-2 gap-5 mb-6">
            <Queue title="Needs Action" items={d.needs_action} tone="bg-[#C49A5A]/15 text-[#8a6a2e]" nav={nav} empty="No items need your attention. You're caught up." />
            <Queue title="New" items={d.new} tone="bg-[#9F634E]/12 text-[#9F634E]" nav={nav} empty="No new orders waiting." />
            <Queue title="Active" items={d.active} tone="bg-[#667A66]/12 text-[#4d6b4d]" nav={nav} empty="No active clients right now." />
            <Queue title="Follow-Up" items={d.follow_up || []} tone="bg-[#8BA0C4]/15 text-[#4a5f86]" nav={nav} empty="No follow-ups due." />
            <Queue title="Completed" items={d.completed} tone="bg-[#E5E1D8] text-[#6B665E]" nav={nav} empty="No completed cases yet." />
          </div>
        </>
      )}
    </DashShell>
  );
}
